import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { UserIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useLogin } from '../../hooks/useAuth';

const loginSchema = z.object({
  loginOuEmail: z.string().min(1, 'Login ou email é obrigatório'),
  senha: z.string().min(1, 'Senha é obrigatória'),
  lembrarMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

// Visual conforme design_handoff_login/README.md (hi-fi). Lógica de auth intacta:
// react-hook-form + zod + useLogin (persistência do token tratada no hook).
const inputCls =
  'h-9 w-full rounded-md border border-zinc-300 bg-white pl-9 pr-3 text-sm text-zinc-900 ' +
  'placeholder:text-zinc-400 transition-colors duration-200 ' +
  'focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 ' +
  'dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100';

const LoginForm: React.FC = () => {
  const { mutate: login, isPending, error } = useLogin();
  // Sem fluxo de recuperação no produto — "Esqueci a senha" revela orientação inline.
  const [mostrarAjudaSenha, setMostrarAjudaSenha] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { lembrarMe: true },
  });

  const onSubmit = (data: LoginFormData) => {
    login(data);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-zinc-50 to-zinc-100 p-6 dark:from-zinc-950 dark:to-zinc-950">
      <div className="w-full max-w-sm">
        {/* Lockup da marca */}
        <div className="mb-6 flex flex-col items-center gap-3.5">
          <img src="/icon.svg" alt="UtilyHub" className="h-14 w-14 rounded-2xl" />
          <div className="text-center">
            <h1 className="text-[26px] font-extrabold tracking-wide text-zinc-900 dark:text-zinc-100">
              UtilyHub
            </h1>
            <p className="text-sm text-zinc-500">Gestão com eficiência</p>
          </div>
        </div>

        {/* Card do formulário */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 rounded-xl border border-zinc-200 bg-white p-6 shadow dark:border-zinc-800 dark:bg-zinc-900"
        >
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
            Entrar no sistema
          </h2>

          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Usuário
            </label>
            <div className="relative">
              <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input
                {...register('loginOuEmail')}
                type="text"
                autoComplete="username"
                className={inputCls}
                placeholder="Login ou email"
              />
            </div>
            {errors.loginOuEmail && (
              <p className="mt-1 text-sm text-rose-500">{errors.loginOuEmail.message}</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Senha
            </label>
            <div className="relative">
              <LockClosedIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input
                {...register('senha')}
                type="password"
                autoComplete="current-password"
                className={inputCls}
                placeholder="••••••••"
              />
            </div>
            {errors.senha && (
              <p className="mt-1 text-sm text-rose-500">{errors.senha.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                {...register('lembrarMe')}
                type="checkbox"
                id="lembrarMe"
                className="h-4 w-4 rounded-sm border-zinc-300 bg-white text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 dark:border-zinc-700 dark:bg-zinc-950"
              />
              <label
                htmlFor="lembrarMe"
                className="ml-2 cursor-pointer select-none text-sm text-zinc-700 dark:text-zinc-300"
              >
                Manter sessão ativa
              </label>
            </div>
            <button
              type="button"
              onClick={() => setMostrarAjudaSenha((v) => !v)}
              className="text-sm font-medium text-blue-600 transition-colors duration-200 hover:underline dark:text-blue-400"
            >
              Esqueci a senha
            </button>
          </div>

          {mostrarAjudaSenha && (
            <p className="rounded-md bg-zinc-50 p-2.5 text-xs text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
              Contate o administrador do sistema pra redefinir sua senha.
            </p>
          )}

          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-500 dark:border-rose-800 dark:bg-rose-900/20">
              {(error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                'Login ou senha inválidos'}
            </div>
          )}

          <button
            type="submit"
            disabled={isPending}
            className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-blue-600 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 dark:focus:ring-offset-zinc-900"
          >
            {isPending && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {isPending ? 'Entrando…' : 'Entrar no sistema'}
          </button>
        </form>

        {/* Nota de rodapé */}
        <p className="mt-[18px] text-center text-xs leading-relaxed text-zinc-400">
          Acesso apenas para usuários autorizados.
          <br />
          Peça pro administrador liberar seu acesso.
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
